import React from 'react';
import { Form, Input, Checkbox, Alert, Tooltip } from '../../../../../components';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { NameField } from '../../cloudAccountFields';
import { TestConnectionButton } from '../../cloudAccountFields';
import TagsSelector from '../../../../TagsSelector';
import './AzureCloudAccount.scss';

/**
 * Azure Cloud Account - Edit Mode
 * Focused component for editing existing Azure cloud accounts
 */
export const AzureCloudAccountEdit = ({
    formData,
    errors,
    onChange,
    onTestConnection,
    testing,
    tested,
    canEditVSphereCredentials,
    showEditVSphereCredentials,
    onToggleVSphereEdit,
    vsphereCredentials,
    onVSphereCredentialChange,
    tags,
    onTagsChange,
    userOrg,
}) => {
    return (
        <>
            <NameField value={formData.name} onChange={onChange('name')} error={errors.name} />

            {/* Credentials Warning */}
            <Alert
                title="Only update credentials if they have changed."
                type="warning"
                showIcon
                icon={<QuestionCircleOutlined />}
                className="azure-credentials-alert"
            />

            <Form.Item
                label={
                    <span>
                        Directory ID{' '}
                        <Tooltip title="Provide your Azure Directory ID. This information can be obtained from the Azure Portal > Azure Active Directory > Properties.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </span>
                }
                validateStatus={errors.tenant_id ? 'error' : ''}
                help={errors.tenant_id}
            >
                <Input
                    value={formData.tenant_id || ''}
                    onChange={onChange('tenant_id')}
                    placeholder="Enter Directory ID (optional)"
                    className={formData.tenant_id ? 'azure-modified-input' : ''}
                />
            </Form.Item>

            <Form.Item
                label={
                    <span>
                        Application ID{' '}
                        <Tooltip title="Enter the username for authentication.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </span>
                }
                validateStatus={errors.username ? 'error' : ''}
                help={errors.username}
            >
                <Input
                    value={formData.username}
                    onChange={onChange('username')}
                    placeholder="Enter Application ID (optional)"
                    className={formData.username ? 'azure-modified-input' : ''}
                />
            </Form.Item>

            <Form.Item
                label={
                    <span>
                        Key Value{' '}
                        <Tooltip title="Enter the password or secret key for authentication.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </span>
                }
                validateStatus={errors.password ? 'error' : ''}
                help={errors.password}
            >
                <Input.Password
                    value={formData.password}
                    onChange={onChange('password')}
                    placeholder="Enter Key Value (optional)"
                    className={formData.password ? 'azure-modified-input' : ''}
                />
            </Form.Item>

            {/* Test Connection - Required for Azure */}
            <TestConnectionButton
                onTest={onTestConnection}
                testing={testing}
                tested={tested}
                tooltip="Connection must be validated before you can Add Target Cloud. The connection and login credentials are tested (SSL certification is ignored)."
                required={false}
            />

            {/* vSphere Credentials Edit */}
            {canEditVSphereCredentials && (
                <>
                    <Form.Item>
                        <Checkbox
                            checked={showEditVSphereCredentials}
                            onChange={(e) => onToggleVSphereEdit(e.target.checked)}
                        >
                            Edit Source vSphere Environment Credentials
                        </Checkbox>
                    </Form.Item>

                    {showEditVSphereCredentials && (
                        <div className="azure-vsphere-credentials-box">
                            <h5 className="azure-vsphere-credentials-heading">Edit Credentials</h5>

                            <Form.Item label="Username">
                                <Input
                                    value={vsphereCredentials.username}
                                    onChange={(e) => onVSphereCredentialChange('username', e.target.value)}
                                    placeholder="Enter vSphere Username"
                                />
                            </Form.Item>

                            <Form.Item label="Password">
                                <Input.Password
                                    value={vsphereCredentials.password}
                                    onChange={(e) => onVSphereCredentialChange('password', e.target.value)}
                                    placeholder="Enter vSphere Password"
                                />
                            </Form.Item>
                        </div>
                    )}
                </>
            )}

            {/* Tags */}
            <Form.Item label="Tags">
                <TagsSelector value={tags} onChange={onTagsChange} userOrg={userOrg} />
            </Form.Item>
        </>
    );
};
